import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
} from '@mui/material';
import { format } from 'date-fns';
import { getRegistration, updateRegistration } from '../services/api';
import { Registration } from '../models/types';

const EditRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { registrationId } = useParams<{ registrationId: string }>();
  
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [formData, setFormData] = useState({
    isConfirmed: false,
    hasAttended: false,
    actualMinutesWorked: 0,
  });

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!registrationId) return;
      
      try {
        setLoading(true);
        
        // This endpoint needs to be implemented in your API and service
        const data = await getRegistration(registrationId);
        setRegistration(data);
        
        setFormData({
          isConfirmed: data.isConfirmed,
          hasAttended: data.hasAttended,
          actualMinutesWorked: data.actualMinutesWorked || 0,
        });
        
      } catch (err) {
        console.error('Error fetching registration details:', err);
        setLoadError('Failed to load registration details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [registrationId]);

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const parsedValue = parseInt(value, 10);
    if (!isNaN(parsedValue) && parsedValue >= 0) {
      setFormData({ ...formData, [name]: parsedValue });
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!registrationId) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    
    try {
      await updateRegistration(registrationId, {
        isConfirmed: formData.isConfirmed,
        hasAttended: formData.hasAttended,
        actualMinutesWorked: formData.actualMinutesWorked,
      });
      
      setSubmitSuccess(true);
      
      // Update the local registration state
      setRegistration(prev => {
        if (!prev) return null;
        return {
          ...prev,
          isConfirmed: formData.isConfirmed,
          hasAttended: formData.hasAttended,
          actualMinutesWorked: formData.actualMinutesWorked,
        };
      });
      
    } catch (err) {
      console.error('Failed to update registration:', err);
      setSubmitError('Failed to update registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading registration details...
        </Typography>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{loadError}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/admin')}>
            Back to Admin Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  if (!registration) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Registration not found</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/admin')}>
            Back to Admin Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin')}
              sx={{ mb: 1 }}
            >
              Back to Admin Dashboard
            </Button>
            <Typography variant="h4">
              Edit Registration
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Registration Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Volunteer:</strong> {registration.volunteerName}
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Job:</strong> {registration.jobName}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Timeslot:</strong> {registration.timeslotStart ? 
                    format(new Date(registration.timeslotStart), 'MMM d, h:mm a') + ' - ' +
                    format(new Date(registration.timeslotEnd || registration.timeslotStart), 'h:mm a') : 
                    'Unknown'}
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Registration Date:</strong> {
                    format(new Date(registration.registeredAt), 'MMM d, yyyy')}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                <strong>Current Status:</strong>
              </Typography>
              {registration.hasAttended ? (
                <Chip label="Attended" color="success" size="small" />
              ) : registration.isConfirmed ? (
                <Chip label="Confirmed" color="primary" size="small" />
              ) : (
                <Chip label="Pending" color="warning" size="small" />
              )}
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Update Status
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Registration updated successfully!
            </Alert>
          )}
          
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isConfirmed}
                    onChange={handleSwitchChange}
                    name="isConfirmed"
                    color="primary"
                  />
                }
                label="Confirmed"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasAttended}
                    onChange={handleSwitchChange}
                    name="hasAttended"
                    color="success"
                    disabled={!formData.isConfirmed}
                  />
                }
                label="Attended"
              />
              
              <Box>
                <TextField
                  fullWidth
                  id="actualMinutesWorked"
                  label="Actual Minutes Worked"
                  name="actualMinutesWorked"
                  type="number"
                  InputProps={{ inputProps: { min: 0 } }}
                  value={formData.actualMinutesWorked}
                  onChange={handleNumberChange}
                  disabled={!formData.hasAttended}
                  helperText="Only set this value if the volunteer has attended"
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default EditRegistrationPage;

